import { motion } from "framer-motion";

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

export default function Story() {
  return (
    <section id="story" className="relative bg-[#F8F9FA] py-24 md:py-36 px-5 md:px-10" data-testid="story-section">
      <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 md:gap-16">
        <div className="md:col-span-5">
          <motion.p {...reveal} className="text-xs uppercase tracking-[0.3em] font-semibold text-electric mb-6">
            The Candidate
          </motion.p>
          <motion.h2
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.1 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02] text-navy"
          >
            From Humble
            <span className="block italic font-medium text-electric">Beginnings,</span>
            For Our People
          </motion.h2>
          <motion.div
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.2 }}
            className="mt-10"
          >
            <div className="relative overflow-hidden rounded-sm border border-navy/10 shadow-[0_32px_64px_-24px_rgba(10,25,47,0.35)]">
              <img
                src="/images/dave-portrait.jpg"
                alt="Darwin Dave Williams, candidate for MHA Nation Chairman"
                className="w-full h-[420px] object-cover object-top"
                loading="lazy"
              />
              <p className="absolute bottom-4 left-4 text-[10px] uppercase tracking-[0.25em] text-white bg-navy/60 backdrop-blur-sm px-3 py-1.5 rounded-full">
                Darwin "Dave" Williams
              </p>
            </div>
          </motion.div>
        </div>

        <div className="md:col-span-7 md:pt-16 space-y-6 text-navy/80 text-base md:text-lg leading-relaxed">
          <motion.p {...reveal} transition={{ ...reveal.transition, delay: 0.1 }}>
            Darwin "Dave" Williams was raised on the Fort Berthold Reservation, where he learned early
            that a community is only as strong as the care it shows its elders and the chances it gives
            its youth. His story is the story of so many of our families — hard work, humility, and an
            unshakable love for the Mandan, Hidatsa and Arikara Nation.
          </motion.p>
          <motion.p {...reveal} transition={{ ...reveal.transition, delay: 0.12 }}>
            Dave has spent his life in service: listening first, showing up always, and doing the quiet
            work that keeps a Nation moving forward. He believes leadership is not a title — it is a
            responsibility carried on behalf of every segment, every district, every family. Now he's
            asking for your trust and your vote, so that together we can build a future rooted in who we
            are — and worthy of who our grandchildren will become.
          </motion.p>
          <motion.p {...reveal} transition={{ ...reveal.transition, delay: 0.15 }}>
            Dave was born in 1959, the first of two children to Sylvia Grey Wolfe, daughter of Agnes
            Dancing Bull. He was raised in New Town surrounded by the love and guidance of his mother,
            grandmother, and great-grandmother — early years that shaped his understanding of belonging,
            responsibility, and the importance of knowing where one comes from. His Mandan name is
            Ii-Daac (ē-deesh), "Cherries in the Mouth," and his connection to New Town and the Shell
            Creek community has remained central throughout his life.
          </motion.p>
          <motion.p {...reveal} transition={{ ...reveal.transition, delay: 0.2 }}>
            David began his professional career in 1979 with Chevron/Gulf Oil &amp; Gas, spending twelve
            years working throughout the petroleum industry — work-over rigs, drilling rigs, production,
            and pipeline operations. It was an education acquired not in theory, but through the
            day-to-day realities of the field.
          </motion.p>
          <motion.p {...reveal} transition={{ ...reveal.transition, delay: 0.22 }}>
            In the 1990s, David attended North Dakota State University and served as an assistant
            baseball coach from 1994 to 1999. He returned to New Town and devoted nearly a decade to
            education and youth athletics — helping establish high school baseball in New Town for the
            first time in 2000, leading the 2004 Legion team to a 37–4 record as Region 7 High School
            Coach of the Year, coaching football and basketball, and working in special education.
          </motion.p>
          <motion.blockquote
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.25 }}
            className="border-l-2 border-gold pl-6 py-1 font-serif italic text-xl md:text-2xl text-navy"
          >
            "No land, no leases and no money."
            <span className="block mt-3 text-xs uppercase tracking-[0.25em] font-sans not-italic font-semibold text-electric">
              — Dave, on Missouri River Resources' beginnings
            </span>
          </motion.blockquote>
          <motion.p {...reveal} transition={{ ...reveal.transition, delay: 0.27 }}>
            In 2007, David became Chief Executive Officer of Missouri River Resources, the MHA Nation's
            tribally owned oil and gas company. Over sixteen years, the company drilled eight successful
            wells, surpassed two million barrels of oil produced, and grew into an $80 million
            enterprise. In 2019, Native Business Magazine recognized him among its Top 30 CEOs in
            Indian Country.
          </motion.p>
          <motion.p {...reveal} transition={{ ...reveal.transition, delay: 0.3 }}>
            His service extends beyond his career — for two and a half years he volunteered in Bible
            study at the North Dakota State Penitentiary, and in 2021 he became the North Dakota State
            Senior Chess Champion, achieving an Expert rating. David and his wife, Valarie, have been
            married for 28 years, with four adult children and ten grandchildren — making the question
            of what one generation leaves to the next deeply personal.
          </motion.p>
          <motion.p {...reveal} transition={{ ...reveal.transition, delay: 0.33 }}>
            In July 2025, while attending Sunday service at New Horizons Church in Four Bears, the
            thought of running for Chairman came to him. Today he brings more than four decades of
            experience in industry, education, youth development, and tribal enterprise to his candidacy —
            believing that economic development must create lasting benefit, leadership must remain
            accountable to the people, and decisions made today must be measured by what they leave for
            generations to come. His hope for those generations is clear: a safe environment, a strong
            spiritual foundation, and an economic engine capable of creating opportunity for all tribal
            members.
          </motion.p>
          <motion.p {...reveal} transition={{ ...reveal.transition, delay: 0.36 }} className="font-serif italic text-lg md:text-xl text-navy">
            Creating a Shared Vision for the MHA Nation.
          </motion.p>
        </div>
      </div>
    </section>
  );
}
